import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent } from '../components/ui/dialog';
import { SEOHead, SEO_CONFIG } from '../components/SEOHead';
import { ReviewSubmissionForm } from '../components/ReviewSubmissionForm';
import { 
  Phone, MessageCircle, ArrowRight, Star, Quote, BadgeCheck, Play,
  Video, Users, Award, ChevronLeft, ChevronRight, X, Instagram, Filter
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Star Rating Component
const StarRating = ({ rating, size = 'md' }) => {
  const sizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
    lg: 'w-6 h-6'
  };
  
  return (
    <div className="flex gap-0.5">
      {[...Array(5)].map((_, i) => (
        <Star 
          key={i} 
          className={`${sizeClasses[size]} ${i < rating ? 'text-yellow-400 fill-yellow-400' : 'text-slate-300'}`} 
        />
      ))}
    </div>
  );
};

// Extract video IDs
const extractYoutubeId = (url) => {
  const match = url?.match(/(?:youtube\.com\/(?:watch\?v=|shorts\/)|youtu\.be\/)([A-Za-z0-9_-]+)/);
  return match ? match[1] : null;
};

const extractInstagramId = (url) => {
  const match = url?.match(/instagram\.com\/(?:reel|p)\/([A-Za-z0-9_-]+)/);
  return match ? match[1] : null;
};

export const Reviews = () => {
  const [reviews, setReviews] = useState([]);
  const [videoReviews, setVideoReviews] = useState([]);
  const [clientLogos, setClientLogos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, 5star, 4star, video
  const [videoModal, setVideoModal] = useState({ open: false, review: null });
  const [showSubmitForm, setShowSubmitForm] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [reviewsRes, logosRes] = await Promise.all([
          axios.get(`${API}/reviews`),
          axios.get(`${API}/client-logos`)
        ]);
        
        const allReviews = reviewsRes.data;
        setVideoReviews(allReviews.filter(r => r.is_video_testimonial));
        setReviews(allReviews.filter(r => !r.is_video_testimonial));
        setClientLogos(logosRes.data);
      } catch (error) {
        console.error('Error fetching reviews:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const whatsappUrl = `https://wa.me/918512853955?text=${encodeURIComponent('Hi, I saw your reviews and I am interested in your services.')}`;

  // Filter reviews
  const filteredReviews = reviews.filter(review => {
    if (filter === 'all') return true;
    if (filter === '5star') return review.rating === 5;
    if (filter === '4star') return review.rating >= 4;
    return true;
  });

  // Calculate stats
  const totalReviews = reviews.length + videoReviews.length;
  const avgRating = totalReviews > 0 
    ? (([...reviews, ...videoReviews].reduce((sum, r) => sum + r.rating, 0)) / totalReviews).toFixed(1)
    : '5.0';

  // SEO config for reviews page
  const reviewsSEO = {
    title: "Customer Reviews & Testimonials | Raj Aluminum & Glass Hardware",
    description: "Read customer feedback and satisfied client testimonials. See why customers trust Raj Aluminum & Glass for quality aluminium and glass work in Faridabad.",
    keywords: "customer feedback, client testimonials, satisfied clients, customer reviews, glass work reviews, aluminium work testimonials, Faridabad reviews"
  };

  return (
    <div data-testid="reviews-page">
      <SEOHead {...reviewsSEO} />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-28 bg-slate-900">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 text-center">
          <p className="text-orange-400 font-semibold mb-4 flex items-center justify-center gap-2">
            <BadgeCheck className="w-5 h-5" /> CUSTOMER REVIEWS & TESTIMONIALS
          </p>
          <h1 className="font-['Oswald'] text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
            Hear from Our Satisfied Clients
          </h1>
          
          {/* Rating Display */}
          <div className="inline-flex flex-col items-center bg-white/10 backdrop-blur-sm rounded-2xl px-8 py-6 mb-8">
            <div className="flex items-center gap-1 mb-2">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-8 h-8 text-yellow-400 fill-yellow-400" />
              ))}
            </div>
            <p className="text-5xl font-['Oswald'] font-bold text-white mb-1">{avgRating}</p>
            <p className="text-slate-400">Based on {totalReviews} Reviews</p>
          </div>

          <p className="text-lg text-slate-300 max-w-2xl mx-auto mb-8">
            Don't just take our word for it. See real feedback from our satisfied customers 
            about their experience with our quality aluminium and glass solutions.
          </p>

          <Button 
            onClick={() => setShowSubmitForm(true)}
            size="lg"
            className="bg-orange-500 hover:bg-orange-600"
            data-testid="submit-review-hero-btn"
          >
            <Star className="w-5 h-5 mr-2" /> Submit Your Review
          </Button>
        </div>
      </section>

      {/* Trust Stats Banner */}
      <section className="py-8 bg-sky-600">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-wrap items-center justify-center gap-8 md:gap-16 text-white">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                <Star className="w-6 h-6 fill-yellow-400 text-yellow-400" />
              </div>
              <div>
                <p className="text-2xl font-bold">{avgRating}</p>
                <p className="text-sm text-sky-100">Average Rating</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                <Users className="w-6 h-6" />
              </div>
              <div>
                <p className="text-2xl font-bold">{totalReviews}+</p>
                <p className="text-sm text-sky-100">Happy Clients</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                <Video className="w-6 h-6" />
              </div>
              <div>
                <p className="text-2xl font-bold">{videoReviews.length}</p>
                <p className="text-sm text-sky-100">Video Testimonials</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                <Award className="w-6 h-6" />
              </div>
              <div>
                <p className="text-2xl font-bold">100%</p>
                <p className="text-sm text-sky-100">Verified Reviews</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Video Testimonials Section */}
      {videoReviews.length > 0 && (
        <section className="py-16 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900" data-testid="video-testimonials-section">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-10">
              <p className="text-pink-400 font-semibold mb-2 flex items-center justify-center gap-2">
                <Play className="w-5 h-5" /> VIDEO TESTIMONIALS
              </p>
              <h2 className="font-['Oswald'] text-3xl md:text-4xl font-bold text-white mb-4">
                Watch What Our Clients Say
              </h2>
              <div className="w-20 h-1 bg-gradient-to-r from-pink-500 to-purple-500 rounded-full mx-auto"></div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {videoReviews.map((review) => {
                const youtubeId = extractYoutubeId(review.video_url);
                const thumbnail = youtubeId 
                  ? `https://img.youtube.com/vi/${youtubeId}/maxresdefault.jpg`
                  : null;

                return (
                  <div 
                    key={review.id}
                    className="relative aspect-[9/16] rounded-xl overflow-hidden cursor-pointer group bg-slate-800"
                    onClick={() => setVideoModal({ open: true, review })}
                    data-testid={`video-review-${review.id}`}
                  >
                    {thumbnail ? (
                      <img 
                        src={thumbnail} 
                        alt={review.name}
                        className="w-full h-full object-cover"
                        loading="lazy"
                        onError={(e) => {
                          if (youtubeId) {
                            e.target.src = `https://img.youtube.com/vi/${youtubeId}/hqdefault.jpg`;
                          }
                        }}
                      />
                    ) : review.video_type === 'instagram' ? (
                      <div className="w-full h-full bg-gradient-to-br from-purple-600 via-pink-500 to-orange-400 flex flex-col items-center justify-center">
                        <Instagram className="w-12 h-12 text-white mb-2" />
                        <span className="text-white/80 text-sm">Video Testimonial</span>
                      </div>
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-sky-600 to-sky-800 flex items-center justify-center">
                        <Video className="w-12 h-12 text-white/50" />
                      </div>
                    )}
                    
                    {/* Play overlay */}
                    <div className="absolute inset-0 bg-black/30 group-hover:bg-black/50 transition-colors flex items-center justify-center">
                      <div className="w-14 h-14 bg-white/90 rounded-full flex items-center justify-center transform scale-90 group-hover:scale-100 transition-transform shadow-lg">
                        <Play className="w-6 h-6 text-slate-800 ml-1" />
                      </div>
                    </div>
                    
                    {/* Info overlay */}
                    <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/90 via-black/60 to-transparent">
                      <StarRating rating={review.rating} size="sm" />
                      <p className="text-white font-semibold mt-2">{review.name}</p>
                      {review.position && (
                        <p className="text-slate-300 text-xs">{review.position}</p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {videoReviews.length > 4 && (
              <div className="text-center mt-8">
                <Button variant="outline" className="border-white/30 text-white hover:bg-white/10">
                  Watch More Videos <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Text Reviews Section */}
      <section className="py-20 bg-slate-50" data-testid="reviews-grid">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-10">
            <div>
              <h2 className="font-['Oswald'] text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                Customer Testimonials
              </h2>
              <div className="w-20 h-1 bg-gradient-to-r from-sky-500 to-orange-500 rounded-full"></div>
            </div>
            
            {/* Filter Buttons */}
            <div className="flex items-center gap-2 mt-4 md:mt-0">
              <Filter className="w-4 h-4 text-slate-500" />
              {[
                { value: 'all', label: 'All' },
                { value: '5star', label: '5 Star' },
                { value: '4star', label: '4+ Star' }
              ].map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => setFilter(opt.value)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                    filter === opt.value
                      ? 'bg-sky-500 text-white'
                      : 'bg-white text-slate-600 hover:bg-slate-100 border'
                  }`}
                  data-testid={`filter-${opt.value}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[...Array(6)].map((_, i) => (
                <Card key={i} className="p-6">
                  <div className="flex gap-1 mb-4">
                    {[...Array(5)].map((_, j) => (
                      <div key={j} className="w-5 h-5 bg-slate-200 rounded animate-pulse"></div>
                    ))}
                  </div>
                  <div className="h-24 bg-slate-200 rounded animate-pulse mb-4"></div>
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-slate-200 rounded-full animate-pulse"></div>
                    <div>
                      <div className="h-4 w-24 bg-slate-200 rounded animate-pulse mb-2"></div>
                      <div className="h-3 w-16 bg-slate-200 rounded animate-pulse"></div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredReviews.map((review) => (
                <Card 
                  key={review.id} 
                  className="hover:shadow-lg transition-shadow"
                  data-testid={`review-card-${review.id}`}
                >
                  <CardContent className="p-6">
                    <Quote className="w-10 h-10 text-sky-200 mb-3" />
                    <StarRating rating={review.rating} />
                    
                    <p className="text-slate-600 mt-4 mb-6 leading-relaxed">
                      "{review.short_quote || review.review}"
                    </p>

                    <div className="flex items-center gap-4 pt-4 border-t border-slate-100">
                      {review.customer_image ? (
                        <img 
                          src={review.customer_image} 
                          alt={review.name}
                          className="w-12 h-12 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-12 h-12 bg-gradient-to-br from-sky-500 to-sky-600 rounded-full flex items-center justify-center text-white font-bold text-lg">
                          {review.name.charAt(0)}
                        </div>
                      )}
                      <div className="flex-1">
                        <p className="font-semibold text-slate-900">{review.name}</p>
                        {review.position ? (
                          <p className="text-sm text-slate-500">{review.position}</p>
                        ) : (
                          <div className="flex items-center gap-2 text-sm text-slate-500">
                            <img 
                              src="https://www.google.com/favicon.ico" 
                              alt="Google" 
                              className="w-4 h-4"
                            />
                            <span>{review.source} Review</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {filteredReviews.length === 0 && !loading && (
            <div className="text-center py-12">
              <p className="text-slate-500">No reviews match your filter. Try a different option.</p>
            </div>
          )}
        </div>
      </section>

      {/* Why Clients Trust Us - Logos */}
      {clientLogos.length > 0 && (
        <section className="py-16 bg-white" data-testid="client-logos-section">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-10">
              <p className="text-orange-500 font-semibold mb-2">TRUSTED BY</p>
              <h2 className="font-['Oswald'] text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                Why Clients Trust Us
              </h2>
              <div className="w-20 h-1 bg-gradient-to-r from-sky-500 to-orange-500 rounded-full mx-auto"></div>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-8 md:gap-16">
              {clientLogos.map((logo) => (
                <div 
                  key={logo.id}
                  className="grayscale hover:grayscale-0 opacity-50 hover:opacity-100 transition-all duration-300"
                  data-testid={`client-logo-${logo.id}`}
                >
                  {logo.website ? (
                    <a href={logo.website} target="_blank" rel="noopener noreferrer">
                      <img 
                        src={logo.logo_url} 
                        alt={logo.name}
                        className="h-16 md:h-20 w-auto object-contain"
                      />
                    </a>
                  ) : (
                    <img 
                      src={logo.logo_url} 
                      alt={logo.name}
                      className="h-16 md:h-20 w-auto object-contain"
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Submit Review CTA */}
      <section className="py-20 bg-gradient-to-br from-sky-600 to-sky-700" data-testid="submit-review-cta">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="font-['Oswald'] text-3xl md:text-4xl font-bold text-white mb-4">
            Share Your Experience
          </h2>
          <p className="text-sky-100 text-lg mb-8 max-w-2xl mx-auto">
            We value your feedback! If you've used our services, we'd love to hear about your experience.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button 
              size="lg" 
              onClick={() => setShowSubmitForm(true)}
              className="bg-white text-sky-700 hover:bg-sky-50"
              data-testid="submit-review-cta-btn"
            >
              <Star className="w-5 h-5 mr-2" /> Submit Your Review
            </Button>
            <a href={whatsappUrl} target="_blank" rel="noopener noreferrer">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
                <MessageCircle className="w-5 h-5 mr-2" /> Chat on WhatsApp
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-8 bg-slate-900 rounded-2xl p-8 md:p-12">
            <div>
              <h3 className="font-['Oswald'] text-2xl md:text-3xl font-bold text-white mb-2">
                Ready to Get Started?
              </h3>
              <p className="text-slate-400">
                Join our satisfied customers. Contact us for a free consultation.
              </p>
            </div>
            <div className="flex flex-wrap gap-4">
              <a href="tel:+918512853955">
                <Button size="lg" className="bg-orange-500 hover:bg-orange-600">
                  <Phone className="w-5 h-5 mr-2" /> Call Now
                </Button>
              </a>
              <Link to="/contact">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
                  Get Free Quote <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Video Modal */}
      <Dialog open={videoModal.open} onOpenChange={(open) => !open && setVideoModal({ open: false, review: null })}>
        <DialogContent className="max-w-2xl p-0 overflow-hidden bg-black border-0">
          {videoModal.review && (
            <div className="relative">
              <Button
                variant="ghost"
                size="icon"
                className="absolute top-2 right-2 z-10 text-white hover:bg-white/20"
                onClick={() => setVideoModal({ open: false, review: null })}
              >
                <X className="w-5 h-5" />
              </Button>

              {videoModal.review.video_type === 'youtube' && (
                <div className="aspect-video w-full">
                  <iframe
                    src={`https://www.youtube.com/embed/${extractYoutubeId(videoModal.review.video_url)}?autoplay=1`}
                    className="w-full h-full"
                    frameBorder="0"
                    allowFullScreen
                    allow="autoplay; encrypted-media"
                  />
                </div>
              )}

              {videoModal.review.video_type === 'instagram' && (
                <div className="aspect-[9/16] w-full max-h-[80vh]">
                  <iframe
                    src={`https://www.instagram.com/reel/${extractInstagramId(videoModal.review.video_url)}/embed`}
                    className="w-full h-full"
                    frameBorder="0"
                    allowFullScreen
                  />
                </div>
              )}

              {videoModal.review.video_type === 'direct' && (
                <video 
                  src={videoModal.review.video_url} 
                  controls 
                  autoPlay
                  className="w-full"
                />
              )}

              <div className="p-4 bg-slate-900">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-sky-500 to-sky-600 rounded-full flex items-center justify-center text-white font-bold">
                    {videoModal.review.name.charAt(0)}
                  </div>
                  <div>
                    <p className="text-white font-semibold">{videoModal.review.name}</p>
                    {videoModal.review.position && (
                      <p className="text-slate-400 text-sm">{videoModal.review.position}</p>
                    )}
                  </div>
                  <div className="ml-auto">
                    <StarRating rating={videoModal.review.rating} size="sm" />
                  </div>
                </div>
                {videoModal.review.review && (
                  <p className="text-slate-300 text-sm mt-3">"{videoModal.review.review}"</p>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Submit Review Modal */}
      <Dialog open={showSubmitForm} onOpenChange={setShowSubmitForm}>
        <DialogContent className="max-w-lg p-0 overflow-hidden">
          <ReviewSubmissionForm 
            onSuccess={() => {
              setTimeout(() => setShowSubmitForm(false), 2000);
            }}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Reviews;
